import { useMemo } from "react"
import {
  Box,
  CenteredLoader,
  ConnectedIcon,
  DisconnectedIcon,
  Link,
  InfoIconWithTooltip,
  Flex,
  Separator,
  Text,
  Tooltip,
  Title,
} from "@shared/primitive-components"
import { ScreenContent } from "@shared/application-components"
import * as styles from "./styles/overallConnectivityStyles"

const TooltipContent = () => (
  <Box css={styles.tooltipContent}>
    <Text>This bar represents the overall connectivity for your managed policies.</Text>
    <Text>
      The green portion shows the percentage of vehicles connected and transmitting data. The red portion represents
      disconnected vehicles not providing data, potentially affecting coverage accuracy.
    </Text>
  </Box>
)

export const OVERALL_CONNECTIVITY_DTI = {
  CONNECTED_SEGMENT_DTI: `overall_connectivity_connected_segment`,
  CONNECTED_MARKER_DTI: `overall_connectivity_connected_marker`,
  DISCONNECTED_SEGMENT_DTI: `overall_connectivity_disconnected_segment`,
  DISCONNECTED_MARKER_DTI: `overall_connectivity_disconnected_marker`,
}

export type OverallConnectivityProps = Readonly<{
  connectivityRate: number | null
  loading?: boolean
  dataTestId?: string
}>

export const OverallConnectivity = ({ connectivityRate, dataTestId, loading }: OverallConnectivityProps) => {
  const roundedConnPercentage: number = useMemo(
    () => (connectivityRate === null ? 0 : Math.floor(connectivityRate * 100)),
    [connectivityRate]
  )

  return (
    <ScreenContent
      dataTestIds={{ root: dataTestId }}
      title={
        <Box css={styles.connectivityContent}>
          <Title tag="h2" variant="headline2">
            Your customers&apos; overall connectivity
            <Separator size="block" color="yellow" />
          </Title>
          <Flex alignItems="center" position="absolute" top="0" css={styles.right0}>
            <Link target="_blank" href="https://help.flockcover.com/understanding-flock-connectivity" css={styles.link}>
              Learn more about connectivity
            </Link>
            <InfoIconWithTooltip tooltipAlign="left" content={TooltipContent} />
          </Flex>
          {loading || connectivityRate === null ? (
            <CenteredLoader />
          ) : (
            <Box css={styles.relative}>
              {roundedConnPercentage > 0 ? (
                <Flex
                  data-testid={OVERALL_CONNECTIVITY_DTI.CONNECTED_MARKER_DTI}
                  css={styles.connectivityMarkerGreen(0)}
                >
                  <ConnectedIcon />
                </Flex>
              ) : null}
              {roundedConnPercentage < 100 ? (
                <Flex
                  data-testid={OVERALL_CONNECTIVITY_DTI.DISCONNECTED_MARKER_DTI}
                  css={styles.connectivityMarkerRed(roundedConnPercentage)}
                >
                  <DisconnectedIcon />
                </Flex>
              ) : null}
              <Flex height="16px" css={styles.connectivityBar}>
                {roundedConnPercentage > 0 ? (
                  <Tooltip content="Connected">
                    <Box
                      data-testid={OVERALL_CONNECTIVITY_DTI.CONNECTED_SEGMENT_DTI}
                      width={`${roundedConnPercentage}%`}
                      css={styles.bgGreen}
                    >
                      <Text css={styles.connectivityText}>{`${roundedConnPercentage}%`}</Text>
                    </Box>
                  </Tooltip>
                ) : null}
                {roundedConnPercentage < 100 ? (
                  <Tooltip content="Disconnected">
                    <Box
                      data-testid={OVERALL_CONNECTIVITY_DTI.DISCONNECTED_SEGMENT_DTI}
                      width={`${100 - roundedConnPercentage}%`}
                      css={styles.bgRed}
                    >
                      <Text css={styles.connectivityText}>{`${100 - roundedConnPercentage}%`}</Text>
                    </Box>
                  </Tooltip>
                ) : null}
              </Flex>
            </Box>
          )}
        </Box>
      }
    />
  )
}
